import { AfterViewInit, ChangeDetectionStrategy, Component, effect, inject, input, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { take } from 'rxjs';

import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';

import { ChatPrescriptionListComponent } from '@lib/chat/components/chat-prescriptions-list/chat-prescription-list.component';
import { ChatNavigationService } from '@lib/chat/core/services/chat-navigation.service';
import { ChatStateService } from '@lib/chat/services/chat-state.service';

import { MemberProfile } from '@lib/core/models/member.interface';

import { ChatUserProfileComponent } from '../chat-user-profile/chat-user-profile.component';

enum ChatTabs {
  profile = 0,
  prescriptions = 1,
}

@Component({
  selector: 'chat-info-board',
  standalone: true,
  imports: [MatTabGroup, MatTab, ChatUserProfileComponent, ChatUserProfileComponent, ChatPrescriptionListComponent],
  styles: `
    :host {
      @apply h-full;
    }
  `,
  templateUrl: './chat-info-board.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInfoBoardComponent implements AfterViewInit {
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private chatNavigationService = inject(ChatNavigationService);
  protected readonly chatState = inject(ChatStateService);

  member = input.required<MemberProfile>();

  protected selectedTabIndex = signal<number>(ChatTabs.profile);
  protected prescriptionList = viewChild('prescriptionList', { read: ChatPrescriptionListComponent });

  constructor() {
    effect(() => {
      const targetTab = this.chatNavigationService.activeTab();
      this.selectedTabIndex.set(targetTab);
    });

    effect(() => {
      const target = this.chatNavigationService.targetPrescriptionId();
      if (target) setTimeout(() => this.prescriptionList()?.highlightPrescription(target), 100);
    });
  }

  ngAfterViewInit(): void {
    this.setInitialTab();
  }

  private setInitialTab() {
    return this.route.queryParams.pipe(take(1)).subscribe(params => {
      const tab = params['tab'];

      if (tab) {
        const tabIndex = ChatTabs[tab as keyof typeof ChatTabs] ?? ChatTabs.profile;
        this.selectedTabIndex.set(tabIndex);
        this.chatNavigationService.setActiveTab(tabIndex);
      }
    });
  }

  protected onTabChange(event: MatTabChangeEvent): void {
    const tabName = ChatTabs[event.index];
    this.selectedTabIndex.set(event.index);
    this.chatNavigationService.setActiveTab(event.index);
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: tabName } });
  }
}
